import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useNotificationCounts } from "@/hooks/use-notification-counts";
import { useSoftLaunch } from "@/hooks/use-soft-launch";
import { Heart, Search, MessageCircle, User as UserIcon, LogOut, Menu, X, Settings, Crown, Sparkles, ExternalLink, Shield } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { KinderwensReminderBanner } from "@/components/kinderwens-reminder-banner";
import { openAnalyticsSettings } from "@/lib/analytics";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function Layout({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, logout } = useAuth();
  const isAdmin = isAuthenticated && (user?.role === "admin" || user?.isAdmin === true);
  const isFreeTier = isAuthenticated && (!user?.subscriptionTier || user.subscriptionTier === "free");
  const isGold = isAuthenticated && user?.subscriptionTier === "gold";
  const isPremium = isAuthenticated && user?.subscriptionTier === "premium";
  const [location] = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { unreadMessages, newMatches } = useNotificationCounts();
  const { softLaunch } = useSoftLaunch();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navItems = isAuthenticated ? [
    { label: "Ontdekken", href: "/ontdekken", icon: Search, badge: 0 },
    { label: "Matches", href: "/matches", icon: Heart, badge: softLaunch ? 0 : newMatches },
    { label: "Berichten", href: "/berichten", icon: MessageCircle, badge: softLaunch ? 0 : unreadMessages },
  ] : [];

  const getInitials = (name?: string) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen flex flex-col w-full relative">
      <header className="sticky top-0 z-50 w-full bg-card/95 backdrop-blur-lg border-b border-border/50 supports-[backdrop-filter]:bg-card/80 warm-shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 w-full">
            
            <Link href="/" className="flex items-center gap-3 group">
              <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                <Heart className="w-6 h-6 fill-current" />
              </div>
              <span className="font-serif font-bold text-xl sm:text-2xl text-foreground tracking-tight">
                kinderwens<span className="text-primary">-dating</span><span className="text-muted-foreground text-base sm:text-lg">.nl</span>
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-4 lg:gap-8">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.startsWith(item.href);
                return (
                  <Link 
                    key={item.href} 
                    href={item.href}
                    className={cn(
                      "flex items-center gap-2 text-sm font-medium transition-all duration-200 hover:text-primary",
                      isActive ? "text-primary border-b-2 border-primary py-7" : "text-muted-foreground py-7"
                    )}
                  >
                    <span className="relative">
                      <Icon className="w-4 h-4" />
                      {item.badge > 0 && (
                        <span className="absolute -top-2 -right-2 min-w-[16px] h-4 bg-destructive text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5 leading-none">
                          {item.badge > 99 ? "99+" : item.badge}
                        </span>
                      )}
                    </span>
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            <div className="hidden md:flex items-center gap-4">
              <a
                href="https://kinderwens-dating.nl"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                data-testid="link-marketing-site"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Naar website
              </a>
              {isFreeTier && (
                <Link href="/prijzen">
                  <Button
                    size="sm"
                    className="bg-gradient-to-r from-primary to-pink-500 hover:from-primary/90 hover:to-pink-500/90 text-white shadow-md shadow-primary/30 hover:shadow-lg hover:shadow-primary/40 transition-all hover:-translate-y-0.5 gap-1.5"
                  >
                    <Crown className="w-3.5 h-3.5" />
                    Upgrade
                  </Button>
                </Link>
              )}
              {(isGold || isPremium) && (
                <Link href="/prijzen">
                  <span className={cn(
                    "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold tracking-wide",
                    isGold
                      ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-700"
                      : "bg-primary/10 text-primary border border-primary/30"
                  )}>
                    {isGold ? <Crown className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
                    {isGold ? "Gold" : "Premium"}
                  </span>
                </Link>
              )}
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-3 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all hover:opacity-80">
                      <Avatar className="h-9 w-9 border-2 border-primary/20">
                        <AvatarImage src={user?.profilePhotoUrl ?? undefined} alt={user?.displayName} />
                        <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                          {getInitials(user?.displayName)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-medium text-foreground">{user?.displayName}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link href="/profiel" className="flex items-center gap-2 cursor-pointer">
                        <UserIcon className="w-4 h-4" />
                        Profiel
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/profiel/instellingen" className="flex items-center gap-2 cursor-pointer">
                        <Settings className="w-4 h-4" />
                        Instellingen
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link href="/admin" className="flex items-center gap-2 cursor-pointer" data-testid="link-admin">
                          <Shield className="w-4 h-4" />
                          Beheer
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => logout()}
                      className="flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive"
                    >
                      <LogOut className="w-4 h-4" />
                      Uitloggen
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Link href="/inloggen" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
                    Inloggen
                  </Link>
                  <Link href="/registreren">
                    <Button className="bg-primary hover:bg-primary-hover text-white px-6 shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all hover:-translate-y-0.5">
                      Schrijf je in
                    </Button>
                  </Link>
                </>
              )}
            </div>

            <button 
              className="md:hidden p-2 text-muted-foreground hover:text-primary"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-white absolute top-20 left-0 w-full border-t border-border/50 py-4 px-4 flex flex-col gap-2 shadow-2xl">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.startsWith(item.href);
              return (
                <Link 
                  key={item.href} 
                  href={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={cn(
                    "flex items-center gap-3 p-4 rounded-xl text-base font-medium transition-colors",
                    isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"
                  )}
                >
                  <span className="relative">
                    <Icon className="w-5 h-5" />
                    {item.badge > 0 && (
                      <span className="absolute -top-2 -right-2 min-w-[16px] h-4 bg-destructive text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5 leading-none">
                        {item.badge > 99 ? "99+" : item.badge}
                      </span>
                    )}
                  </span>
                  {item.label}
                </Link>
              );
            })}
            
            <div className="h-px bg-border my-2" />

            <a
              href="https://kinderwens-dating.nl"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 p-4 rounded-xl text-base font-medium text-muted-foreground hover:bg-muted"
              data-testid="link-marketing-site-mobile"
            >
              <ExternalLink className="w-5 h-5" />
              Naar website
            </a>

            <div className="h-px bg-border my-2" />

            {isAuthenticated ? (
              <>
                {isFreeTier && (
                  <Link
                    href="/prijzen"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-4 rounded-xl text-base font-semibold bg-gradient-to-r from-primary/10 to-pink-500/10 text-primary hover:from-primary/20 hover:to-pink-500/20 transition-colors"
                  >
                    <Crown className="w-5 h-5" />
                    Upgrade naar Premium
                  </Link>
                )}
                {(isGold || isPremium) && (
                  <Link
                    href="/prijzen"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={cn(
                      "flex items-center gap-3 p-4 rounded-xl text-base font-semibold transition-colors",
                      isGold
                        ? "bg-amber-100/60 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                        : "bg-primary/10 text-primary hover:bg-primary/20"
                    )}
                  >
                    {isGold ? <Crown className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
                    {isGold ? "Gold abonnement actief" : "Premium abonnement actief"}
                  </Link>
                )}
                <Link
                  href="/profiel"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 p-4 rounded-xl text-base font-medium text-muted-foreground hover:bg-muted"
                >
                  <UserIcon className="w-5 h-5" />
                  Profiel
                </Link>
                <Link
                  href="/profiel/instellingen"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 p-4 rounded-xl text-base font-medium text-muted-foreground hover:bg-muted"
                >
                  <Settings className="w-5 h-5" />
                  Instellingen
                </Link>
                {isAdmin && (
                  <Link
                    href="/admin"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-4 rounded-xl text-base font-medium text-muted-foreground hover:bg-muted"
                    data-testid="link-admin-mobile"
                  >
                    <Shield className="w-5 h-5" />
                    Beheer
                  </Link>
                )}
                <button 
                  onClick={() => { logout(); setIsMobileMenuOpen(false); }}
                  className="flex items-center gap-3 p-4 rounded-xl text-base font-medium text-destructive hover:bg-destructive/10 w-full text-left"
                >
                  <LogOut className="w-5 h-5" />
                  Uitloggen
                </button>
              </>
            ) : (
              <div className="flex flex-col gap-3 p-2">
                <Link href="/inloggen" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full justify-center h-12">Inloggen</Button>
                </Link>
                <Link href="/registreren" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button className="w-full justify-center h-12 bg-primary">Schrijf je in</Button>
                </Link>
              </div>
            )}
          </div>
        )}
      </header>

      <KinderwensReminderBanner />

      <main className="flex-1 w-full relative z-10 flex flex-col">
        {children}
      </main>
      
      <footer className="w-full border-t border-border/50 bg-background/50 py-8 mt-auto z-10">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-muted-foreground space-y-3">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link href="/voorwaarden" className="hover:text-primary transition-colors">
              Algemene Voorwaarden
            </Link>
            <Link href="/privacy" className="hover:text-primary transition-colors">
              Privacybeleid
            </Link>
            <button
              type="button"
              onClick={openAnalyticsSettings}
              className="hover:text-primary transition-colors"
              data-testid="open-analytics-settings"
            >
              Cookie-instellingen
            </button>
          </div>
          <div>
            &copy; {new Date().getFullYear()} kinderwens-dating.nl &mdash; Daten voor singles met een serieuze kinderwens.
          </div>
        </div>
      </footer>
    </div>
  );
}
